import { useState } from "react";
import { mutate } from "swr";
import useToast from "~/components/ui/toast";
import {
  FeaturedSession,
  FeaturedSessionCreate,
  featureSession,
  projectFeaturedSessionsPath,
  unfeatureSession,
} from "~/services/projects";

export interface UseFeaturedSessionsMutations {
  isMutating: boolean;
  // Pins a session to the rail, or refreshes its snapshot if its share token is
  // already on it. Returns the created/refreshed row, or null on failure.
  feature: (input: FeaturedSessionCreate) => Promise<FeaturedSession | null>;
  // Unpins a rail entry, leaving its share intact.
  unfeature: (entry: FeaturedSession) => Promise<void>;
}

// Shared curate-the-rail mutations for a project's featured sessions, backing
// both the sidebar/sessions-list "Unfeature session" action and the
// coupled-to-sharing toggle in ShareSessionBody. Centralizing the optimistic
// cache patch here means every surface that curates the rail agrees on how.
export const useFeaturedSessionsMutations = (projectId: string): UseFeaturedSessionsMutations => {
  const { showError, showSuccess } = useToast();
  const [isMutating, setIsMutating] = useState(false);
  const key = projectFeaturedSessionsPath(projectId);

  const feature = async (input: FeaturedSessionCreate) => {
    const optimisticId = `pending-${input.share_token}`;
    const optimistic: FeaturedSession = {
      id: optimisticId,
      project_id: projectId,
      share_token: input.share_token,
      display_name: input.display_name,
      created_at: new Date().toISOString(),
      agent_id: input.agent_id ?? null,
    };
    setIsMutating(true);
    void mutate(key, (current: FeaturedSession[] | undefined) => [...(current ?? []), optimistic], {
      revalidate: false,
    });
    try {
      const created = await featureSession(projectId, input);
      void mutate(
        key,
        (current: FeaturedSession[] | undefined) =>
          current?.map((f) => (f.id === optimisticId ? created : f)),
        { revalidate: false }
      );
      showSuccess("Added to the project's Featured Sessions.");
      return created;
    } catch {
      void mutate(
        key,
        (current: FeaturedSession[] | undefined) => current?.filter((f) => f.id !== optimisticId),
        { revalidate: false }
      );
      showError("Couldn't feature this session");
      return null;
    } finally {
      setIsMutating(false);
    }
  };

  const unfeature = async (entry: FeaturedSession) => {
    setIsMutating(true);
    void mutate(
      key,
      (current: FeaturedSession[] | undefined) => current?.filter((f) => f.id !== entry.id),
      { revalidate: false }
    );
    try {
      await unfeatureSession(projectId, entry.id);
      showSuccess("Removed from Featured Sessions.");
    } catch {
      void mutate(
        key,
        (current: FeaturedSession[] | undefined) => (current ? [...current, entry] : [entry]),
        { revalidate: false }
      );
      showError("Couldn't remove from Featured Sessions");
    } finally {
      setIsMutating(false);
    }
  };

  return { isMutating, feature, unfeature };
};
